/**
 * Custom launcher for the einbuergerungstest app.
 *
 * Replaces the generic CEFR-chip + Practice/Review/Archive layout with a
 * shape tailored to the citizenship-test domain:
 *   - Header with the current Bundesland chip and a link to settings.
 *   - Four tiles: Practice · Simulate test · Browse questions · Vocabulary.
 *     The last one leaves this app entirely, for the sibling
 *     einbuergerungstest-vokabeln launcher. It is a separate app with its own
 *     streak and stats, but nobody arrives at a vocabulary trainer except
 *     through the exam that motivated it, so it is discoverable from both the
 *     main launcher and here.
 *   - A "Simulations" summary card (taken / passed / pass rate / best).
 *   - The standard activity heatmap.
 *
 * Counts are sourced from /api/apps (app-wide totals for learning/known) and
 * /api/apps/:id/stats (simulations). The Bundesland comes from
 * /api/apps/:id/config.
 */
import {
  ArrowLeft,
  BookA,
  BookOpenCheck,
  ClipboardCheck,
  GraduationCap,
  Landmark,
  Library,
  TriangleAlert,
} from 'lucide-react';
import { Link } from 'react-router';

import { ActivityHeatmap } from '@/components/stats/ActivityHeatmap';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AppHomeSkeleton } from '@/components/ui/skeletons';
import { BUNDESLAND_NAMES, type BundeslandCode, type EinbuergerungsConfig } from '@/core/types';
import { useApps, useAppConfig, useAppStats, useLibrary } from '@/lib/queries';

import type { AppHomeProps } from '../types';

/** The sibling vocabulary app, linked from the fourth tile. */
const VOKABELN_APP_ID = 'einbuergerungstest-vokabeln';

export function EinbuergerungstestAppHome({ appId }: AppHomeProps) {
  const appsQuery = useApps();
  const statsQuery = useAppStats(appId);
  const configQuery = useAppConfig<EinbuergerungsConfig>(appId);
  // For the bookmark badge — independent of the main library filter set.
  const bookmarksQuery = useLibrary(appId, { bookmark: 'only' });

  if (appsQuery.isLoading) {
    return (
      <div className="min-h-screen">
        <header className="border-border bg-card border-b">
          <div className="mx-auto h-[60px] max-w-3xl px-4 py-4 sm:px-6" />
        </header>
        <AppHomeSkeleton />
      </div>
    );
  }
  if (appsQuery.error) {
    return <ErrorMessage>{appsQuery.error.message}</ErrorMessage>;
  }

  const app = appsQuery.data?.apps.find((a) => a.id === appId);
  if (!app || !app.stats) {
    return <ErrorMessage>Couldn't load this app.</ErrorMessage>;
  }

  const stats = app.stats;
  const bundesland = configQuery.data?.config?.bundesland ?? 'BW';
  const bookmarkCount = bookmarksQuery.data?.cards.length ?? 0;

  // The sibling vocabulary app. Read from the already-cached /api/apps
  // response rather than a second query. Absent (or not yet migrated) → the
  // tile is simply not rendered.
  const vokabelnApp = appsQuery.data?.apps.find((a) => a.id === VOKABELN_APP_ID);
  const vokabelnStats = vokabelnApp?.status === 'active' ? vokabelnApp.stats : null;
  const totalQuestions = stats.learning + stats.known + stats.archived;
  const sims = statsQuery.data?.simulations ?? null;
  const strugglingCount = statsQuery.data?.struggling_count ?? 0;

  return (
    <div className="min-h-screen">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
          <div className="flex flex-1 items-center gap-2">
            <Landmark className="text-primary size-5" />
            <h1 className="font-display text-xl font-semibold">{app.display_name}</h1>
          </div>
          <Link
            to="/settings?tab=einbuergerungstest"
            className="focus-visible:ring-ring rounded-md transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:outline-none"
          >
            <Badge variant="secondary" className="gap-1">
              <span className="text-muted-foreground">State:</span>
              {BUNDESLAND_NAMES[bundesland as BundeslandCode] ?? bundesland}
            </Badge>
          </Link>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10">
        <section className="grid gap-3 sm:grid-cols-2">
          <LauncherTile
            to={`/apps/${appId}/practice`}
            icon={BookOpenCheck}
            title="Practice"
            subtitle={`${stats.learning} questions to learn`}
            disabled={stats.learning === 0}
            tone="primary"
          />
          <LauncherTile
            to={`/apps/${appId}/simulation`}
            icon={GraduationCap}
            title="Simulate test"
            subtitle={
              sims
                ? `${sims.total_taken} taken · ${Math.round(sims.pass_rate * 100)}% pass`
                : 'Take the exam'
            }
            disabled={totalQuestions === 0}
            tone="accent"
          />
          <LauncherTile
            to={`/apps/${appId}/questions`}
            icon={Library}
            title="Browse questions"
            subtitle={
              bookmarkCount > 0
                ? `${totalQuestions} total · ${bookmarkCount} bookmarked`
                : `${totalQuestions} total`
            }
            disabled={totalQuestions === 0}
            tone="muted"
          />
          {/* Appears only once something is flagged. This app has its own tile
              grid, so the generic AppHomeRoute's mistakes button never reaches
              it — the mode has to be wired into both launchers. */}
          {strugglingCount > 0 && (
            <LauncherTile
              to={`/apps/${appId}/mistakes`}
              icon={TriangleAlert}
              title="Keeps getting you"
              subtitle={`${strugglingCount} ${strugglingCount === 1 ? 'question' : 'questions'} missed 3× in a row`}
              tone="muted"
            />
          )}
          {vokabelnStats && (
            <LauncherTile
              to={`/apps/${VOKABELN_APP_ID}`}
              icon={BookA}
              title="Vocabulary"
              subtitle={
                vokabelnStats.learning > 0
                  ? `${vokabelnStats.learning} words to learn`
                  : 'All words learned'
              }
              tone="muted"
            />
          )}
        </section>

        <SimulationsCard sims={sims} />

        {statsQuery.data?.activity_heatmap ? (
          <section>
            <h2 className="text-muted-foreground mb-2 text-sm font-medium">
              Activity (last 12 weeks)
            </h2>
            <ActivityHeatmap data={statsQuery.data.activity_heatmap} />
          </section>
        ) : null}

        <div className="text-center">
          <Link
            to={`/apps/${appId}/stats`}
            className="text-primary text-sm font-medium underline-offset-4 hover:underline"
          >
            View detailed stats →
          </Link>
        </div>
      </main>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function LauncherTile({
  to,
  icon: Icon,
  title,
  subtitle,
  disabled,
  tone,
}: {
  to: string;
  icon: typeof BookOpenCheck;
  title: string;
  subtitle: string;
  disabled?: boolean;
  tone: 'primary' | 'accent' | 'muted';
}) {
  const toneRing =
    tone === 'primary'
      ? 'border-primary/30 bg-primary/5 hover:border-primary/60'
      : tone === 'accent'
        ? 'border-accent/40 bg-accent/10 hover:border-accent/70'
        : 'border-border bg-card hover:border-primary/40';

  const inner = (
    <div
      className={`flex h-full flex-col gap-2 rounded-2xl border-2 p-5 transition-colors ${toneRing} ${
        disabled ? 'pointer-events-none opacity-40' : ''
      }`}
    >
      <Icon className="text-primary size-6" />
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <p className="text-muted-foreground text-sm">{subtitle}</p>
    </div>
  );

  if (disabled) return inner;
  return (
    <Link
      to={to}
      className="focus-visible:ring-ring rounded-2xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
    >
      {inner}
    </Link>
  );
}

function SimulationsCard({ sims }: { sims: import('@/core/types').SimulationStatsSummary | null }) {
  if (!sims || sims.total_taken === 0) {
    return (
      <section className="border-border rounded-2xl border border-dashed p-6 text-center">
        <ClipboardCheck className="text-muted-foreground mx-auto size-6" />
        <h2 className="font-display mt-2 text-lg font-semibold">No simulations yet</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Take the first one to track your pass rate over time.
        </p>
      </section>
    );
  }

  const passRatePct = Math.round(sims.pass_rate * 100);
  const avgScore = sims.average_correct.toFixed(1);

  return (
    <section className="border-border bg-card rounded-2xl border p-5">
      <header className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Simulations</h2>
        <span className="text-muted-foreground text-xs">last 10 shown</span>
      </header>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Metric value={sims.total_taken} label="Taken" />
        <Metric value={sims.total_passed} label="Passed" />
        <Metric value={`${passRatePct}%`} label="Pass rate" />
        <Metric
          value={sims.best_total > 0 ? `${sims.best_correct}/${sims.best_total}` : '—'}
          label="Best score"
        />
      </div>
      <div className="mt-4">
        <p className="text-muted-foreground mb-2 text-xs">
          Score trend · avg {avgScore}/{sims.trend[0]?.total ?? '?'}
        </p>
        <ScoreSparkline trend={sims.trend} />
      </div>
    </section>
  );
}

function Metric({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-display text-2xl font-semibold">{value}</span>
      <span className="text-muted-foreground text-xs tracking-wider uppercase">{label}</span>
    </div>
  );
}

function ScoreSparkline({
  trend,
}: {
  trend: import('@/core/types').SimulationStatsSummary['trend'];
}) {
  if (trend.length === 0) return null;
  const W = 280;
  const H = 36;
  const max = Math.max(...trend.map((t) => t.total));
  const stepX = trend.length > 1 ? W / (trend.length - 1) : 0;
  const points = trend
    .map((t, i) => {
      const x = i * stepX;
      const y = H - (t.correct / max) * (H - 4);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="w-full">
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        className="text-primary"
      />
      {trend.map((t, i) => (
        <circle
          key={t.sim_id}
          cx={i * stepX}
          cy={H - (t.correct / max) * (H - 4)}
          r={3}
          className={t.passed ? 'fill-primary' : 'fill-destructive'}
        />
      ))}
    </svg>
  );
}

function ErrorMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <p className="text-destructive">{children}</p>
    </div>
  );
}
